import React, { useMemo, useState } from 'react';
import { ShotMapPanel } from './MatchInsightPanels';
import { buildPlayerInsight, formatInsightTime } from '../../utils/playerInsight';

const formatNumber = (value, digits = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(digits) : '—';
};

function EventList({ events, onSeek, emptyText }) {
  if (!events?.length) return <div className="player-insight-empty">{emptyText}</div>;
  return (
    <div className="player-insight-event-list">
      {events.map((event, index) => (
        <button
          type="button"
          className={`player-insight-event tone-${event.tone || 'neutral'}`}
          key={`${event.id}-${index}`}
          onClick={() => Number.isFinite(event.tick) && onSeek?.(event.tick)}
          disabled={!Number.isFinite(event.tick)}
        >
          <span className="player-insight-event-time">{event.timeLabel}</span>
          <span className="player-insight-event-icon">{event.type === 'goal' || event.type === 'conceded-goal' ? '◆' : event.type === 'assist' ? 'A' : event.type === 'shot' ? '⊙' : event.type === 'key-pass' ? '↗' : event.type === 'duel' ? '⚔' : event.type === 'save' ? '▣' : event.type === 'recovery' ? '↺' : event.type === 'interception' ? '↔' : event.type === 'block' ? '▰' : event.type === 'tackle' ? '✚' : event.type === 'sweeper' ? '⌁' : '·'}</span>
          <span className="player-insight-event-copy">
            <strong>{event.label}</strong>
            <small>{event.detail}</small>
          </span>
          <span className="player-insight-event-arrow">›</span>
        </button>
      ))}
    </div>
  );
}

function PossessionList({ chains, onSeek }) {
  if (!chains?.length) return <div className="player-insight-empty">No possession chains include this player.</div>;
  return (
    <div className="player-insight-chain-list">
      {chains.map((chain) => (
        <button type="button" className={`player-insight-chain result-${chain.result}`} key={chain.id} onClick={() => Number.isFinite(chain.startTick) && onSeek?.(chain.startTick)} disabled={!Number.isFinite(chain.startTick)} title="Play from 0.3 seconds before this possession">
          <span className="player-insight-chain-time">{formatInsightTime(chain.startSeconds)}</span>
          <span className="player-insight-chain-copy">
            <span className="player-insight-chain-flow" aria-label={chain.players.join(' to ') || 'Team possession'}>
              {chain.players.length ? chain.players.map((name, index) => (
                <span className="player-insight-chain-step" key={`${name}-${index}`}>
                  {index > 0 && <span className="player-insight-chain-arrow" aria-hidden="true">→</span>}
                  <span className={`player-insight-chain-player${name.toLowerCase() === chain.focusPlayer.toLowerCase() ? ' is-focus' : ''}`} dir="auto">{name}</span>
                </span>
              )) : <span className="player-insight-chain-player">Team possession</span>}
            </span>
            <small>{chain.result === 'goal' ? 'Ended in a goal' : chain.result === 'turnover' ? 'Possession lost' : 'Possession retained'} · {chain.touches} {chain.touches === 1 ? 'touch' : 'touches'} by {chain.focusPlayer}</small>
          </span>
          <span className="player-insight-event-arrow">›</span>
        </button>
      ))}
    </div>
  );
}

export default function PlayerInsightContent({ match, player, rating = null, onSeek = null, showSummary = true }) {
  const [view, setView] = useState('timeline');
  const insight = useMemo(() => buildPlayerInsight(match, player, rating), [match, player, rating]);
  const counts = insight?.counts || {};
  const tabs = [
    ['timeline', 'Timeline', insight?.timeline?.length || 0],
    ['shots', 'Shots', insight?.shots?.length || 0],
    ['key-passes', 'Key passes', insight?.keyPasses?.length || 0],
    ['chains', 'Possessions', insight?.possessionChains?.length || 0],
    ['duels', 'Duels', insight?.duels?.length || 0],
    ['defense', 'Defensive actions', insight?.defensiveActions?.length || 0],
    ...(insight?.isKeeper ? [['saves', 'Saves', insight?.saveMap?.length || 0]] : []),
  ];
  const metrics = [
    ['Goals', counts.goals],
    ['Assists', counts.assists],
    ['xG', formatNumber(counts.xg, 2)],
    ['Key passes', counts.keyPasses],
    ['Progressive passes', counts.progressivePasses],
    ['Touches', counts.touches],
    ['Recoveries', counts.recoveries],
    ['Turnovers', counts.turnovers],
  ];
  return (
    <div className="player-insight-content">
      {showSummary && <div className="player-insight-summary-grid">
        {metrics.map(([label, value]) => <div className="player-insight-summary" key={label}><span>{label}</span><strong>{value}</strong></div>)}
      </div>}
      <div className="player-insight-tabs" role="tablist" aria-label="Player evidence views">
        {tabs.map(([key, label, count]) => <button type="button" role="tab" aria-selected={view === key} className={view === key ? 'active' : ''} key={key} onClick={() => setView(key)}>{label}<span>{count}</span></button>)}
      </div>
      {view === 'timeline' && <EventList events={insight.timeline} onSeek={onSeek} emptyText="No player events were recorded." />}
      {view === 'shots' && (
        <div className="player-insight-shot-view">
          <ShotMapPanel match={match} shots={insight.shots} onSeek={onSeek} title="Player shot map" compact />
          <div className="player-insight-shot-timeline">
            <div className="player-insight-shot-timeline-title">Shots timeline</div>
            <EventList events={insight.shots} onSeek={onSeek} emptyText="No shots were recorded." />
          </div>
        </div>
      )}
      {view === 'key-passes' && (
        <EventList events={insight.keyPasses} onSeek={onSeek} emptyText="No key passes were recorded." />
      )}
      {view === 'duels' && <EventList events={insight.duels} onSeek={onSeek} emptyText="No duels were recorded." />}
      {view === 'defense' && <EventList events={insight.defensiveActions} onSeek={onSeek} emptyText="No defensive actions were recorded." />}
      {view === 'saves' && insight.isKeeper && (
        <div className="player-insight-shot-view">
          <ShotMapPanel
            match={match}
            shots={insight.saveMap}
            onSeek={onSeek}
            title="Goalkeeper saves"
            compact
            legend={<><span><i className="shot-arrow-goal" /> Save</span><span><i className="shot-arrow-red" /> Goal conceded</span></>}
          />
          <div className="player-insight-shot-timeline">
            <div className="player-insight-shot-timeline-title">Saves timeline</div>
            <EventList events={insight.saveMap} onSeek={onSeek} emptyText="No saves or conceded goals were recorded." />
          </div>
        </div>
      )}
      {view === 'chains' && <PossessionList chains={insight.possessionChains} onSeek={onSeek} />}
    </div>
  );
}
